import React, { useEffect, useRef, useState } from 'react'
import { useContextHook } from 'use-context-hook'
import { StyledNewOnboarding, StyledUserMessage } from './NewOnboarding.styles'
import { AuthContext } from '../../../Context/authContext'
import OnboardingHeader from './OnboardingHeader'
import BotMessage from './BotMessage'
import ChatInput from './ChatInput'
import ProcessingSteps from './ProcessingSteps'
import ProspectsResult from './ProspectsResult'
import LinkedinFlowReady from './LinkedinFlowReady'
import CompanyInfoCard from './CompanyInfoCard'
import ConnectLinkedIn from './ConnectLinkedIn'
import CampaignReady from './CampaignReady'
import EmbeddedOfferForm from './EmbeddedOfferForm'
import onboardingService from '../../../services/onboardingService'
import offerService from '../../../services/offerService'
import { loadProgress, saveProgress, clearProgress } from './onboardingProgress'
import { ONBOARDING_FLOW, PROSPECTS_NEXT_OPTIONS, LINKEDIN_TEMPLATE, TEMPLATE_MESSAGES } from './onboardingFlow'
import useIcpProspects from './useIcpProspects'
import useCampaignLaunch from './useCampaignLaunch'

const buildIcpFromOffer = (offer) => {
    if (!offer) return ''
    const mainGoal = (offer.outcomes || []).filter(Boolean)[0]
    const mainPain = (offer.core_features || []).map(feature => feature?.pain_point).filter(Boolean)[0]
    const traits = []
    if (mainPain) traits.push(`facing challenges with ${mainPain}`)
    if (mainGoal) traits.push(`looking to ${mainGoal}`)
    if (traits.length) return `Decision-makers at companies ${traits.join(' and ')}`
    return offer.promoting ? `Companies that would benefit from ${offer.promoting}` : ''
}

const resolveProspectsItems = (icpResult) => {
    if (icpResult.loading) return null
    if (Array.isArray(icpResult.items)) return icpResult.items
    return []
}

const resolveProspectsMessage = (icpResult, fallbackMessage) => {
    const hasRealProspects = Array.isArray(icpResult.items) && icpResult.items.length > 0
    const count = icpResult.total ?? (Array.isArray(icpResult.items) ? icpResult.items.length : 0)
    if (hasRealProspects) return `I found ${count} ${count === 1 ? 'lead' : 'leads'} matching your offer and target market.\n\nReview the list below. Once you're happy with it, I'll create personalized outreach for each prospect.`
    if (icpResult.loading) return fallbackMessage
    return "I couldn't find prospects for this ICP yet. Try refining it and searching again."
}

const replaceAtIndex = (list, targetIndex, patch) =>
    list.map((item, index) => (index === targetIndex ? { ...item, ...patch } : item))

const NewOnboarding = () => {
    const { user } = useContextHook(AuthContext, v => ({ user: v.user }))
    const fullName = user?.data?.fullName || user?.fullName || ''
    const firstName = fullName.trim().split(/\s+/)[0] || 'there'

    const personalizeMessages = messages =>
        messages?.map(message => message.replace(/\{\{\s*firstName\s*\}\}/g, firstName))

    const [saved] = useState(loadProgress)

    const [history, setHistory] = useState(saved?.history?.length ? saved.history : [{ type: 'bot', stepId: 'intro' }])
    const [templateMessagesFields, setTemplateMessagesFields] = useState(saved?.templateMessagesFields || TEMPLATE_MESSAGES)
    const [offerData, setOfferData] = useState(saved?.offerData || null)
    const [recommendedIcp, setRecommendedIcp] = useState(saved?.recommendedIcp || '')

    const { icpResult, allProspects, searchProspects, changePage } = useIcpProspects(saved?.icpResult, saved?.allProspects)
    const {
        campaignStatus,
        campaignError,
        setCampaignStatus,
        ensureCampaignCreated,
        finishCampaign,
        failWith,
    } = useCampaignLaunch({
        prospects: allProspects,
        messageValues: templateMessagesFields.map(field => field.value),
        icp: icpResult.icp,
        offer: offerData,
    })

    const endRef = useRef(null)

    useEffect(() => {
        const latestMessage = endRef.current?.previousElementSibling
        if (!latestMessage) return undefined

        const HEADER_CLEARANCE = 140
        const placeLatestMessage = () => {
            const fitsCentered = latestMessage.getBoundingClientRect().height <= window.innerHeight - HEADER_CLEARANCE
            latestMessage.scrollIntoView({ behavior: 'smooth', block: fitsCentered ? 'center' : 'start' })
            return fitsCentered
        }

        if (!placeLatestMessage()) return undefined

        const keepCentered = new ResizeObserver(() => placeLatestMessage())
        keepCentered.observe(latestMessage)
        const stopChasing = setTimeout(() => keepCentered.disconnect(), 12000)
        return () => {
            keepCentered.disconnect()
            clearTimeout(stopChasing)
        }
    }, [history])

    useEffect(() => {
        saveProgress({
            history,
            templateMessagesFields,
            offerData,
            recommendedIcp,
            icpResult: { ...icpResult, loading: false },
            allProspects,
        })
    }, [history, templateMessagesFields, offerData, recommendedIcp, icpResult, allProspects])

    useEffect(() => {
        let active = true
        onboardingService
            .getOnboardingStatus()
            .then(res => {
                if (active && res?.data?.isComplete) {
                    clearProgress()
                    window.location.assign('/dashboard')
                }
            })
            .catch(() => {})
        return () => {
            active = false
        }
    }, [])

    useEffect(() => {
        onboardingService.grantOnboardingCredits().catch(() => {})
    }, [])

    const handleConnectLinkedinFromCampaign = () => {
        setCampaignStatus('idle')
        setHistory(prev => [...prev, { type: 'bot', stepId: 'connect-linkedin' }])
    }

    const goBack = () => {
        setHistory(prev => {
            const lastUserIndex = prev.map(entry => entry.type).lastIndexOf('user')
            if (lastUserIndex < 1) return prev
            const stepIndex = lastUserIndex - 1
            return prev.slice(0, stepIndex + 1).map((entry, index) =>
                index === stepIndex
                    ? { ...entry, pickedOptionId: undefined, submittedText: undefined, formSubmitted: undefined, processingDone: undefined }
                    : entry
            )
        })
    }

    const handleTemplateMessageChange = (id, value) => {
        setTemplateMessagesFields(prev => replaceAtIndex(prev, prev.findIndex(field => field.id === id), { value }))
    }

    const handleCustomSequence = async (entryIndex, option) => {
        setHistory(prev => [
            ...replaceAtIndex(prev, entryIndex, { pickedOptionId: option.id }),
            { type: 'user', text: option.label },
            { type: 'bot', stepId: 'building-custom-sequence' },
        ])
        try {
            const workflowId = await ensureCampaignCreated()
            try {
                await onboardingService.completeOnboarding()
            } catch (err) {
                console.error('Failed to mark onboarding complete:', err)
            }
            clearProgress()
            window.location.assign(`/automation/flow-builder?id=${workflowId}`)
        } catch (err) {
            failWith(err.message || 'Could not build your sequence. Please try again.')
            setHistory(prev => [...prev, { type: 'bot', stepId: 'campaign-ready' }])
        }
    }

    const generateIcpRecommendation = () => {
        setRecommendedIcp('')
        onboardingService
            .generateIcp(offerData)
            .then(res => setRecommendedIcp(res?.data?.icp || buildIcpFromOffer(offerData)))
            .catch(() => setRecommendedIcp(buildIcpFromOffer(offerData)))
    }

    const generateSequenceMessages = () => {
        onboardingService
            .generateMessages({ offer: offerData, icp: recommendedIcp })
            .then(res => {
                const generated = res?.data?.messages || []
                if (!generated.length) return
                setTemplateMessagesFields(prev =>
                    prev.map((field, i) => (generated[i] != null ? { ...field, value: generated[i] } : field))
                )
            })
            .catch(() => {})
    }

    const appendUserChoice = (entryIndex, option) => {
        setHistory(prev => {
            const isProspectsStep = Boolean(ONBOARDING_FLOW[prev[entryIndex]?.stepId]?.prospects)
            const patch = isProspectsStep
                ? {
                    pickedOptionId: option.id,
                    snapshot: { items: icpResult.items, page: icpResult.page, hasMore: icpResult.hasMore },
                }
                : { pickedOptionId: option.id }
            return [
                ...replaceAtIndex(prev, entryIndex, patch),
                { type: 'user', text: option.label },
                { type: 'bot', stepId: option.next },
            ]
        })
    }

    const handleSelect = (entryIndex, option) => {
        if (option?.customSequence) {
            handleCustomSequence(entryIndex, option)
            return
        }
        if (option?.generateIcpFromOffer) generateIcpRecommendation()
        if (option?.generateMessagesFromOffer) generateSequenceMessages()
        appendUserChoice(entryIndex, option)
    }

    const handleSubmitInput = (entryIndex, text, nextStepId) => {
        if (nextStepId === 'processing-icp') {
            searchProspects(text, 1)
        }
        setHistory(prev => {
            const updated = replaceAtIndex(prev, entryIndex, { submittedText: text })
            const tail = [{ type: 'user', text }]
            if (nextStepId) tail.push({ type: 'bot', stepId: nextStepId })
            return [...updated, ...tail]
        })
    }

    const handleProcessingComplete = (entryIndex, nextStepId) => {
        setHistory(prev => {
            if (prev[entryIndex]?.processingDone) return prev
            const updated = replaceAtIndex(prev, entryIndex, { processingDone: true })
            if (nextStepId) updated.push({ type: 'bot', stepId: nextStepId })
            return updated
        })
    }

    const handleOfferFormComplete = (entryIndex, nextStepId) => {
        setHistory(prev => {
            if (prev[entryIndex]?.formSubmitted) return prev
            const updated = replaceAtIndex(prev, entryIndex, { formSubmitted: true })
            updated.push({ type: 'user', text: 'Continue' })
            if (nextStepId) updated.push({ type: 'bot', stepId: nextStepId })
            return updated
        })
    }

    const renderUserMessage = (entry, entryIndex) => (
        <StyledUserMessage key={entryIndex}>
            <span className='bubble'>{entry.text}</span>
        </StyledUserMessage>
    )

    const renderProcessingStep = (entry, step, entryIndex) => {
        if (entry.processingDone) return null
        return (
            <ProcessingSteps
                key={entryIndex}
                title={step.processing.title}
                steps={step.processing.steps}
                stepDuration={step.processing.stepDuration}
                loading={entry.stepId === 'processing-icp' && icpResult.loading}
                onComplete={() => handleProcessingComplete(entryIndex, step.processing.next)}
            />
        )
    }

    const renderProspectsStep = (entry, step, entryIndex) => {
        const isFrozen = Boolean(entry.snapshot)
        const source = entry.snapshot || icpResult
        const hasRealProspects = Array.isArray(source.items) && source.items.length > 0
        return (
            <ProspectsResult
                key={entryIndex}
                message={resolveProspectsMessage(source, step.prospects.message)}
                prospects={resolveProspectsItems(source)}
                totalRows={step.prospects.totalRows}
                total={source.total}
                loading={isFrozen ? false : icpResult.loading}
                paginated={!isFrozen && (hasRealProspects || icpResult.page > 1)}
                page={source.page}
                hasMore={source.hasMore}
                onPageChange={isFrozen ? undefined : changePage}
                options={entry?.pickedOptionId ? [] : PROSPECTS_NEXT_OPTIONS}
                onSelectOption={(opt) => handleSelect(entryIndex, opt)}
            />
        )
    }

    const renderConnectLinkedinStep = (entryIndex) => (
        <ConnectLinkedIn
            key={entryIndex}
            onContinue={() => setHistory(prev => [...prev, { type: 'bot', stepId: 'campaign-ready' }])}
        />
    )

    const renderCampaignReadyStep = (entryIndex) => (
        <CampaignReady
            key={entryIndex}
            status={campaignStatus}
            error={campaignError}
            onLaunch={() => finishCampaign(true)}
            onSaveDraft={() => finishCampaign(false)}
            onConnectLinkedin={handleConnectLinkedinFromCampaign}
        />
    )

    const renderIcpRecommendationStep = (entry, step, entryIndex) => (
        <BotMessage key={entryIndex} messages={personalizeMessages(step?.messages)}>
            {!entry?.submittedText && (
                <ChatInput
                    initialValue={recommendedIcp}
                    placeholder='Describe your ideal customer...'
                    buttonLabel='Search'
                    onSubmit={(text) => handleSubmitInput(entryIndex, text, step?.next)}
                />
            )}
        </BotMessage>
    )

    const renderLinkedinFlowReadyStep = (entry, step, entryIndex) => (
      <BotMessage key={entryIndex} messages={personalizeMessages(step?.messages)}>
          <LinkedinFlowReady
            key={entryIndex}
            title={step.linkedinFlowReady.title}
            message={step.linkedinFlowReady.message}
            templateSteps={LINKEDIN_TEMPLATE.steps}
            templateMessages={templateMessagesFields}
            options={entry?.pickedOptionId ? [] : (step?.options || [])}
            onSelectOption={(opt) => handleSelect(entryIndex, opt)}
          />
      </BotMessage>

    )

    const renderDefaultStep = (entry, step, entryIndex) => {
        const showOptions = !entry?.pickedOptionId && step?.options?.length > 0
        const showInput = step?.input && !entry?.submittedText
        return (
            <BotMessage key={entryIndex} messages={personalizeMessages(step?.messages)}>
                {step?.video?.src && (
                    <div className='video-holder'>
                        <video
                            src={step?.video?.src}
                            poster={step?.video?.poster}
                            controls
                            playsInline
                            preload='metadata'
                        />
                    </div>
                )}
                {step?.messagingMessages && (
                    <CompanyInfoCard
                        fields={templateMessagesFields}
                        onChange={handleTemplateMessageChange}
                        multiline
                    />
                )}
                {step?.offerForm && !entry?.formSubmitted && (
                    <div className='offer-form-holder'>
                        <EmbeddedOfferForm
                            onComplete={(payload) => {
                                setOfferData(payload)
                                // Persist the offer to the user (non-blocking — onboarding continues regardless).
                                offerService.createOffer(payload).catch(err => console.error('Failed to save offer:', err))
                                handleOfferFormComplete(entryIndex, step?.next)
                            }}
                        />
                    </div>
                )}
                {showInput && (
                    <ChatInput
                        variant={step?.input?.variant}
                        placeholder={step?.input?.placeholder}
                        buttonLabel={step?.input?.buttonLabel}
                        initialValue={step?.input?.prefillIcp ? icpResult.icp : ''}
                        onSubmit={(text) => handleSubmitInput(entryIndex, text, step?.input?.next)}
                    />
                )}
                {showOptions && (
                    <div className='btn-holder'>
                        {step?.options?.map(opt => (
                            <button key={opt.id} type='button' onClick={() => handleSelect(entryIndex, opt)}>
                                {opt?.label}
                            </button>
                        ))}
                    </div>
                )}
            </BotMessage>
        )
    }

    const renderHistoryEntry = (entry, entryIndex) => {
        if (entry.type === 'user') return renderUserMessage(entry, entryIndex)

        const step = ONBOARDING_FLOW[entry.stepId]
        if (!step) return null
        if (step?.processing) return renderProcessingStep(entry, step, entryIndex)
        if (step?.prospects) return renderProspectsStep(entry, step, entryIndex)
        if (step?.connectLinkedin) return renderConnectLinkedinStep(entryIndex)
        if (step?.campaignReady) return renderCampaignReadyStep(entryIndex)
        if (step?.icpRecommendation) return renderIcpRecommendationStep(entry, step, entryIndex)
        if (step?.linkedinFlowReady) return renderLinkedinFlowReadyStep(entry, step, entryIndex)
        return renderDefaultStep(entry, step, entryIndex)
    }

    const canGoBack = history.some(entry => entry.type === 'user')

    return (
        <>
            <OnboardingHeader />
            <StyledNewOnboarding>
                <div className='content-holder'>
                    <h1>Hello {firstName}!</h1>
                    <h2>Welcome To Houdiny AI <br /> Your Outbound Sales System</h2>

                    {history?.map(renderHistoryEntry)}

                    {canGoBack && (
                        <div className='back-row'>
                            <button type='button' className='back-btn' onClick={goBack}>
                                ← Back
                            </button>
                        </div>
                    )}

                    <div ref={endRef} />
                </div>
            </StyledNewOnboarding>
        </>
    )
}

export default NewOnboarding
